// ─── src/components/dashboard/CheckInScanner.tsx ─────────────────────────────

import { useEffect, useRef, useState, useCallback } from "react";
import { Html5Qrcode } from "html5-qrcode";
import {
  CheckCircle2, AlertTriangle, XCircle, Camera, CameraOff, Search, Loader2,
} from "lucide-react";
import { useCheckInByToken, useCheckInById, useMeetupAttendees } from "../../hooks/useMeetupAttendees";
import type { CheckInResponse, CheckInResult } from "../../types/meetupAttendee";

const SCANNER_ID = "meetup-checkin-scanner";

/** Visual treatment per outcome — a receptionist glances for half a second,
 *  so colour and size carry the message, not the wording. */
const OUTCOME: Record<CheckInResult, { cls: string; icon: typeof CheckCircle2; label: string }> = {
  ok:                 { cls: "border-emerald-500 bg-emerald-500/15 text-emerald-300", icon: CheckCircle2,   label: "مقبول — Admit" },
  already_checked_in: { cls: "border-amber-500 bg-amber-500/15 text-amber-300",       icon: AlertTriangle,  label: "Already checked in" },
  not_accepted:       { cls: "border-red-500 bg-red-500/15 text-red-300",             icon: XCircle,        label: "Not accepted" },
  wrong_event:        { cls: "border-red-500 bg-red-500/15 text-red-300",             icon: XCircle,        label: "Wrong meetup" },
  not_found:          { cls: "border-red-500 bg-red-500/15 text-red-300",             icon: XCircle,        label: "Unknown ticket" },
};

function formatTime(iso?: string | null) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

export function CheckInScanner({ eventId }: { eventId: string }) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const lastScanRef = useRef<{ token: string; at: number } | null>(null);

  const [isScanning, setIsScanning] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<CheckInResponse | null>(null);
  const [manualSearch, setManualSearch] = useState("");

  const { mutate: checkInToken, isPending: isCheckingToken } = useCheckInByToken();
  const { mutate: checkInId, isPending: isCheckingId } = useCheckInById();

  // Manual fallback list — only queried once the receptionist types something.
  const { data: manualResults } = useMeetupAttendees(
    { eventId, page: 1, limit: 10, search: manualSearch },
    !!eventId && manualSearch.trim().length >= 2,
  );

  const submitToken = useCallback(
    (raw: string) => {
      // Ignore the same code re-read within 3s (the camera fires continuously).
      const now = Date.now();
      if (lastScanRef.current && lastScanRef.current.token === raw && now - lastScanRef.current.at < 3000) {
        return;
      }
      lastScanRef.current = { token: raw, at: now };
      checkInToken({ token: raw, eventId }, { onSuccess: setOutcome });
    },
    [checkInToken, eventId],
  );

  // Start / stop the camera.
  useEffect(() => {
    if (!isScanning) return;
    const scanner = new Html5Qrcode(SCANNER_ID, { verbose: false });
    scannerRef.current = scanner;

    scanner
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decoded) => submitToken(decoded),
        () => undefined, // per-frame decode misses are normal; ignore
      )
      .catch(() => {
        setCameraError("Couldn't open the camera. Check browser permissions, or use manual check-in below.");
        setIsScanning(false);
      });

    return () => {
      scanner.stop().then(() => scanner.clear()).catch(() => undefined);
      scannerRef.current = null;
    };
  }, [isScanning, submitToken]);

  const isBusy = isCheckingToken || isCheckingId;
  const view = outcome ? OUTCOME[outcome.result] : null;
  const Icon = view?.icon;

  return (
    <div className="space-y-4">
      {/* Outcome banner — the thing staff actually look at */}
      {outcome && view && Icon && (
        <div className={`card border-2 p-5 flex items-start gap-4 ${view.cls}`}>
          <Icon size={32} className="shrink-0" />
          <div className="min-w-0">
            <p className="font-bold text-lg leading-tight">{view.label}</p>
            {outcome.attendee && (
              <p className="text-white text-base mt-1 truncate" dir="rtl">
                {outcome.attendee.fullName}
              </p>
            )}
            <p className="text-sm opacity-80 mt-0.5">
              {outcome.message}
              {outcome.result === "already_checked_in" && outcome.attendee?.checkedInAt
                ? ` (${formatTime(outcome.attendee.checkedInAt)})`
                : ""}
            </p>
            {outcome.result === "wrong_event" && outcome.event && (
              <p className="text-xs opacity-70 mt-1">Ticket is for: {outcome.event.title}</p>
            )}
          </div>
        </div>
      )}

      {/* Camera */}
      <div className="card p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <p className="text-white font-semibold text-sm">QR check-in</p>
            <p className="text-brand-muted text-xs mt-0.5">
              Point the camera at the attendee&rsquo;s ticket code.
            </p>
          </div>
          <button
            onClick={() => { setIsScanning((v) => !v); setCameraError(null); }}
            className={isScanning ? "btn-ghost text-sm flex items-center gap-1.5" : "btn-primary text-sm flex items-center gap-1.5"}
          >
            {isScanning ? <><CameraOff size={14} /> Stop</> : <><Camera size={14} /> Start camera</>}
          </button>
        </div>

        {/* html5-qrcode mounts the video feed into this node */}
        <div
          id={SCANNER_ID}
          className={`overflow-hidden rounded-xl bg-brand-navy ${isScanning ? "block" : "hidden"}`}
        />

        {!isScanning && !cameraError && (
          <div className="rounded-xl border border-dashed border-brand-border py-10 text-center text-brand-muted text-sm">
            Camera is off.
          </div>
        )}

        {cameraError && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-400 text-sm">
            {cameraError}
          </div>
        )}

        {isBusy && (
          <p className="text-brand-muted text-xs mt-3 flex items-center gap-1.5">
            <Loader2 size={12} className="animate-spin" /> Checking in…
          </p>
        )}
      </div>

      {/* Manual fallback — venue wifi and cameras both fail in the real world */}
      <div className="card p-5">
        <p className="text-white font-semibold text-sm mb-1">Manual check-in</p>
        <p className="text-brand-muted text-xs mb-3">
          If the code won&rsquo;t scan, find the attendee by name, email or phone.
        </p>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input
            type="text"
            value={manualSearch}
            onChange={(e) => setManualSearch(e.target.value)}
            placeholder="Search this event's attendees…"
            className="w-full pl-9 pr-3 py-2 text-sm bg-brand-navy border border-brand-border rounded-lg text-white placeholder-brand-muted focus:outline-none focus:border-brand-green transition-colors"
          />
        </div>

        {manualSearch.trim().length >= 2 && (
          <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
            {(manualResults?.data ?? []).length === 0 && (
              <p className="text-brand-muted text-xs py-2">No matches.</p>
            )}
            {(manualResults?.data ?? []).map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-brand-border px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-white text-sm truncate" dir="rtl">{a.fullName}</p>
                  <p className="text-brand-muted text-xs truncate">
                    {a.email} · {a.approvalStatus}
                    {a.checkedInAt ? ` · in at ${formatTime(a.checkedInAt)}` : ""}
                  </p>
                </div>
                <button
                  onClick={() => checkInId(a.id, { onSuccess: setOutcome })}
                  disabled={isBusy || !!a.checkedInAt || a.approvalStatus !== "accepted"}
                  className="btn-primary text-xs shrink-0 disabled:opacity-40"
                  title={
                    a.checkedInAt ? "Already checked in"
                    : a.approvalStatus !== "accepted" ? "Not accepted"
                    : "Check in"
                  }
                >
                  {a.checkedInAt ? "In" : "Check in"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
